<template>
	<BaseLayout :footer-full="true">
		<div class="container">
			<h1 class="heading-1 text-align-center mb-100 mb-200-md">Fundrise Pro Membership Agreement</h1>
			<section class="grid-md">
				<article class="col-8-md col-offset-2-md position-relative">
					<div class="module">
						<div class="module-header">
							<h2 class="heading-5">Source Data</h2>
						</div>
						<div class="module-section">
							<p
								>To access the data underlying some of the tools available through Fundrise Pro, please
								<BaseLink
									classes="text-color-current-color text-decoration-underline"
									:link="{ router: 'contact' }"
									>contact us</BaseLink
								>
								to submit an application to be granted access via a public API access key.</p
							>
						</div>
					</div>
					<div class="module">
						<div class="module-header">
							<h2 class="heading-5">Terms & Conditions</h2>
						</div>
						<div class="module-section">
							<p> Date of last revision: March 29, 2024 </p>
							<p>
								This Fundrise Pro Membership Agreement (the “Agreement”) sets forth the terms and
								conditions for a Fundrise, LLC (“Fundrise”) customer (“You”) to open a Fundrise Pro
								account (“Fundrise Pro”), as described herein.
							</p>
							<p>
								Fundrise is offering You Fundrise Pro, an account through which You can receive access
								to certain Fundrise products and services. Specifically, Fundrise Pro features include:
							</p>
							<ul class="ul">
								<li>
									<strong>Direct investment</strong> - Make one-off investments into specific funds,
									including strategy-specific investment opportunities.
								</li>
								<li>
									<strong>Custom investment plan</strong> - Build your own portfolio with full
									allocation control and shape your portfolio to your preferences.
								</li>
								<li>
									<strong>Expert-level data</strong> - Dig deeper into your portfolio's individual
									assets via Basis&trade;, Fundrise's proprietary data warehouse.
								</li>
							</ul>

							<p>
								The Fundrise Pro product is comprised of one tier with a flat monthly membership fee of
								$10, or a flat yearly membership fee of $99. You will be charged at the time You sign up
								and on a calendar month or calendar year cadence thereafter, depending on Your
								membership preference. Membership fee may be waived under certain circumstances.
							</p>

							<p>
								You may cancel Your Fundrise Pro membership at any time within the Fundrise app or
								website. You understand that Fundrise may at any time and in its sole discretion, alter
								the membership fee and add or remove features from Fundrise Pro. Fundrise will provide
								You with 30 days advance written notice of any changes to its membership fee. Such
								notice will be provided by email, Fundrise app or website announcement, push
								notification, or traditional mail. You agree that receipt of Fundrise communication by
								any one of those methods is sufficient notice. Notwithstanding anything herein, we will
								not provide You with prior notice of any amendments to Fundrise Pro that are either
								immaterial or beneficial to You. You agree that, by continuing to participate in
								Fundrise Pro, You will be deemed to have agreed to and accepted the revised Agreement.
							</p>
							<p>
								You understand that this Agreement is not a recommendation to make a specific investment
								or to use any specific investment strategy. Fundrise provides no investment, legal, or
								tax advice through Fundrise Pro.
							</p>
							<p>
								You further understand that Fundrise's provision of certain information through
								Basis&trade; does not constitute (1) information that specifically addresses any
								specific individual's investment objectives, financial situation or the particular needs
								of any specific person who may receive the Basis&trade; data; (2) the establishment of
								an advisory relationship as a result of having access to or using the Basis&trade; data;
								or (3) an offer or solicitation for the purchase or sale of any security subject to the
								information. You additionally understand that neither Fundrise nor any third party is
								responsible for any investment decisions or for any damages or other losses resulting
								from the use of the Basis&trade; data. You also understand that Fundrise provides
								information from various third party sources believed to be reliable, but cannot
								guarantee the accuracy, availability and completeness of that information; and such
								information is provided for informational purposes only. You also understand that past
								performance does not guarantee future results.
							</p>
							<p>
								You understand that if You choose to cancel Your Fundrise Pro membership for any reason,
								You will no longer be charged for Fundrise Pro and You will continue to have access to
								all Fundrise Pro features until the end of Your then-current billing period. You will
								not receive a refund for any pro rata portion of the membership fee applicable to Your
								current billing cycle.
							</p>
							<p>
								You are liable for all claims, expenses, fines and liability we incur arising out of:
								(i) a chargeback, refund, payment error, or other invalid payment You cause; (ii) any
								error, negligence, misconduct, or fraud by You or someone acting on Your behalf; and
								(iii) any losses resulting from Your failure to comply with the terms of this Agreement.
							</p>
							<p>
								You understand that by using Fundrise Pro, You agree to the following: (i) You will use
								Fundrise Pro exclusively for private purposes, in a personal capacity; (ii) You will not
								use Fundrise Pro for commercial purposes, such as commercial securities trading or the
								professional management of third parties' assets, or for activities with a credit
								institution, financial services institution, or any other company which is subject to
								any domestic or foreign banking, stock exchange, securities trading or investment
								supervision; (iii) You will not use Fundrise Pro in any other manner for the purposes of
								third parties; and (iv) You will not distribute information from Fundrise Pro onward to
								third parties and will not make the same available to third parties. The software and
								data available through Fundrise Pro (including, but not limited to, Basis&trade; data)
								is owned by Fundrise, its affiliates, or a third party, and is protected by copyrights,
								database rights, trademarks and/or patents owned by the relevant party. You acknowledge
								that you are being granted a limited, personal, non-exclusive, non-transferable (without
								right of sublicense) right during the term of this Agreement to use the software and
								data available through Fundrise Pro in accordance with the terms of this Agreement. No
								proprietary rights are being transferred with respect to any software or data.
							</p>
							<p>
								In the event Fundrise suspects any prohibited conduct, Fundrise reserves the right, with
								or without notice to You, to delay or suspend all activity.
							</p>
							<h2 class="heading-6 mt-150">Additional Disclosures </h2>
							<p>
								You understand that by using Fundrise Pro, You remain subject to all other terms and
								conditions provided in the Fundrise
								<BaseLink
									classes="text-color-current-color text-decoration-underline"
									:link="termsOfServiceDocLink"
								/>, which are incorporated by reference and constitute a part of this Agreement. You
								also may be subject to additional terms and conditions that may apply when You use
								affiliate or third party services, third party content or third party software. This
								Agreement will be governed by the laws of the State of Delaware without regard to its
								conflict of law provisions, and is subject to the Arbitration Agreement in the Fundrise
								<BaseLink
									classes="text-color-current-color text-decoration-underline"
									:link="termsOfServiceDocLink"
								/>. With respect to any disputes or claims not subject to arbitration, as set forth in
								the Fundrise
								<BaseLink
									classes="text-color-current-color text-decoration-underline"
									:link="termsOfServiceDocLink"
								/>, You and Fundrise agree to submit to the personal and exclusive jurisdiction of (i)
								the United States District Court for the Eastern District of Virginia (Alexandria
								division), or (ii) solely to the extent there is no applicable federal jurisdiction over
								such dispute or matter, in the Circuit Court for Fairfax County, Virginia. The failure
								of Fundrise to exercise or enforce any right or provision of this Agreement will not
								constitute a waiver of such right or provision. If any provision of this Agreement is
								found by a court of competent jurisdiction to be invalid, the parties nevertheless agree
								that the court should endeavor to give effect to the parties' intentions as reflected in
								the provision, and the other provisions of this Agreement remain in full force and
								effect. You agree that regardless of any statute or law to the contrary, any claim or
								cause of action arising out of or related to use of Fundrise Pro or this Agreement must
								be filed within one (1) year after such claim or cause of action arose or be forever
								barred. A printed version of this Agreement and of any notice given in electronic form
								will be admissible in judicial or administrative proceedings based upon or relating to
								this Agreement to the same extent and subject to the same conditions as other business
								documents and records originally generated and maintained in printed form. You may not
								assign this Agreement without the prior written consent of Fundrise, but Fundrise may
								assign or transfer this Agreement, in whole or in part, without restriction.
							</p>
						</div>
					</div>
				</article>
			</section>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { termsOfServiceDocLink } from '@constants/links';
import { useHead } from '@unhead/vue';

useHead({
	title: 'Pro Membership Agreement'
});
</script>
